// TanStack Query keys and hooks for the sender screener. There is no sender event on the stream
// yet, so lists refetch on focus and after every decision.
import { queryOptions, useQuery, type QueryClient } from "@tanstack/react-query";
import { isApiError } from "./api";
import { sendersApi, type SenderListParams } from "./sendersApi";

export const senderKeys = {
  lists: ["senders"] as const,
  list: (params: SenderListParams) => ["senders", params] as const,
  sender: (accountId: string, address: string) =>
    ["sender", accountId, address.toLowerCase()] as const,
};

export function senderListQuery(params: SenderListParams) {
  return queryOptions({
    queryKey: senderKeys.list(params),
    queryFn: ({ signal }) => sendersApi.list(params, signal),
    staleTime: 20_000,
    refetchInterval: 300_000,
  });
}

export function useSenderList(params: SenderListParams) {
  return useQuery(senderListQuery(params));
}

/** One sender; null when MeeMail has no stats for the address yet (404). */
export function useSender(accountId: string | null, address: string | null) {
  return useQuery({
    queryKey: senderKeys.sender(accountId ?? "", address ?? ""),
    queryFn: async ({ signal }) => {
      try {
        return await sendersApi.get(accountId ?? "", address ?? "", signal);
      } catch (error) {
        if (isApiError(error) && error.status === 404) return null;
        throw error;
      }
    },
    enabled: !!accountId && !!address,
    staleTime: 30_000,
  });
}

export function invalidateSenders(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: senderKeys.lists });
  void queryClient.invalidateQueries({ queryKey: ["sender"] });
}
